/**
 * Chatbot behaviour API client — general row, per-channel overrides, effective view and
 * availability (BFF: `/api/v1/store/settings/chatbot[/channels/{channel}|/effective|/availability]`,
 * openapi.yml lines 7946-8081, verified 2026-09-18).
 *
 * Backs `ChatbotSettingsPage` (`/settings/chatbot`, plan-merchant-portal-019 WP2 = plan-conversation-001
 * WP-MP.4). The interim General-tab-only bridge that used to own the general row
 * (`store-settings/api/chatbotGeneralSettingsApi.ts`) is retired — this is its replacement, grown
 * with the per-channel/effective/availability surface the interim card never modelled.
 */
import { apiClient } from '@/shared/api';
import type {
  ChatbotAvailability,
  ChatbotChannel,
  ChatbotChannelSettings,
  ChatbotDictionaries,
  ChatbotGeneralSettings,
  EffectiveChatbotSettings,
  UpdateChatbotChannelSettingsRequest,
  UpdateChatbotGeneralSettingsRequest,
} from '../model/types';

const BASE = '/api/v1/store/settings/chatbot';

export const getChatbotSettings = async (): Promise<ChatbotGeneralSettings> =>
  (await apiClient.get<ChatbotGeneralSettings>(BASE)).data;

/**
 * Merge-semantic on the hub: an absent field means "unchanged". Callers must send only the fields
 * that actually changed (+ `version`) — never the whole resolved resource, or every untouched
 * platform default is materialised into the tenant's row (design §11).
 */
export const updateChatbotSettings = async (
  body: UpdateChatbotGeneralSettingsRequest,
): Promise<ChatbotGeneralSettings> =>
  (await apiClient.put<ChatbotGeneralSettings>(BASE, body)).data;

/** Throws a {@link NormalizedApiError} with `status === 404` when no override exists for this channel
 * — the caller (see `../hooks`) maps that to `null`, "inherits general", never an error state. */
export const getChatbotChannelSettings = async (
  channel: ChatbotChannel,
): Promise<ChatbotChannelSettings> =>
  (await apiClient.get<ChatbotChannelSettings>(`${BASE}/channels/${channel}`)).data;

/** The body is sparse: absent means "inherit the general row", not `null`. */
export const putChatbotChannelSettings = async (
  channel: ChatbotChannel,
  body: UpdateChatbotChannelSettingsRequest,
): Promise<ChatbotChannelSettings> =>
  (await apiClient.put<ChatbotChannelSettings>(`${BASE}/channels/${channel}`, body)).data;

/** 204 — the channel now inherits the general row again ("Reset to general"). */
export const deleteChatbotChannelSettings = async (channel: ChatbotChannel): Promise<void> => {
  await apiClient.delete(`${BASE}/channels/${channel}`);
};

/** Omit `channel` for the general resolution. Flat resolved fields + `overriddenFields[]` — no
 * per-field `source`, no `missingTemplates[]` (contract delta 2026-09-18, design §7.1/§16). */
export const getEffectiveChatbotSettings = async (
  channel?: ChatbotChannel,
): Promise<EffectiveChatbotSettings> =>
  (await apiClient.get<EffectiveChatbotSettings>(`${BASE}/effective`, { params: { channel } }))
    .data;

/**
 * Read-only: the touchpoint kill-switch, the chatbot/AI/hand-off capability (with the platform
 * global mask) and whether a bot is configured, separately per channel — drives the availability
 * strip (design §6.1) and the "Locked by platform" chips on AI mode / hand-off (§9.1).
 */
export const getChatbotAvailability = async (): Promise<ChatbotAvailability> =>
  (await apiClient.get<ChatbotAvailability>(`${BASE}/availability`)).data;

/**
 * *(contract delta 2026-09-23)* The commands/intents/tools/providers vocabulary the selects on this
 * page and the AI provider card are validated against (adr-conversation-013 D2) — codes only, no
 * static fallback (design §17.3.0: the static lists drifted twice).
 */
export const getChatbotDictionaries = async (): Promise<ChatbotDictionaries> =>
  (await apiClient.get<ChatbotDictionaries>(`${BASE}/dictionaries`)).data;
